import React, { useState } from "react";
import {
  Alert,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  TextField,
  Button,
} from "@mui/material";
import { ArrowDownLeft, ArrowUpRight, Wallet, Activity } from "lucide-react";
import useApiEp from "../../hooks/useApiEp";
import MuPb from "../../widgets/MuPb";
import { getErrorResponse } from "../../utils/util";

const RcsUsageAnalytics = () => {
  // State for date range
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Determine if only one of the dates is selected
  const isPartialDate = (startDate && !endDate) || (!startDate && endDate);
  const bothDatesSelected = startDate && endDate;

  // Build the query string dynamically ONLY if both dates are selected
  const queryParams = new URLSearchParams();
  if (bothDatesSelected) {
    queryParams.append("startDate", startDate);
    queryParams.append("endDate", endDate);
  }
  
  const queryString = queryParams.toString();
  const endpointUrl = `/rcs/get-rcs-usage-customers-analytics${
    queryString ? `?${queryString}` : ""
  }`;

  // The hook will re-fetch when the URL string changes
  const { data, isLoading, isError, error } = useApiEp(endpointUrl);

  const analyticsData = data?.data || [];

  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section (Always Visible) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          mb: 3,
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Activity size={32} style={{ marginRight: "12px", color: "#1976d2" }} />
          <Typography variant="h5" component="h1" fontWeight="bold">
            RCS Customer Usage & Balances
          </Typography>
        </Box>

        {/* Date Filter Controls (Always Visible) */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            label="Start Date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
          {(startDate || endDate) && (
            <Button variant="text" color="error" onClick={handleClearDates}>
              Clear
            </Button>
          )}
        </Box>
      </Box>

      {/* Warning for partial date selection */}
      {isPartialDate && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Please select both a Start Date and an End Date to apply the filter.
        </Alert>
      )}

      {/* Main Content Area: Handles Loading, Error, and Table states */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
          <MuPb />
        </Box>
      ) : isError ? (
        <Alert severity="error">{getErrorResponse(error)}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
          <Table sx={{ minWidth: 650 }} aria-label="analytics table">
            <TableHead sx={{ bgcolor: "grey.100" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "bold" }}>Customer ID</TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  Total Credits
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  Total Debits
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  Net Usage
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                  Current Balance
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {analyticsData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No analytics data found for the selected criteria.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                analyticsData
                  .filter((row) => row.customerId != null)
                  .map((row) => (
                    <TableRow
                      key={row.customerId}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                        {row.customerId}
                      </TableCell>

                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            color: "success.main",
                          }}
                        >
                          <ArrowDownLeft size={16} style={{ marginRight: "4px" }} />
                          <Typography variant="body2">
                            {row.totalCreditAmount.toLocaleString()}
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              ({row.creditCount} txns)
                            </Typography>
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            color: "error.main",
                          }}
                        >
                          <ArrowUpRight size={16} style={{ marginRight: "4px" }} />
                          <Typography variant="body2">
                            {row.totalDebitAmount.toLocaleString()}
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 1 }}
                            >
                              ({row.debitCount} txns)
                            </Typography>
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell align="right">
                        <Typography
                          fontWeight="bold"
                          color={row.netUsage >= 0 ? "success.main" : "error.main"}
                        >
                          {row.netUsage > 0 ? "+" : ""}
                          {row.netUsage.toLocaleString()}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            fontWeight: "bold",
                          }}
                        >
                          <Wallet size={16} style={{ marginRight: "8px", color: "#555" }} />
                          {row.currentBalance.toLocaleString()}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default RcsUsageAnalytics;